import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";
import { isProyectosAdminRole } from "../../utils/proyectosRol";
import {
  BURDEN_I_SECUNDARIA_OPCIONES,
  BURDEN_NOMINAL_OPCIONES,
  BURDEN_V_SECUNDARIA_OPCIONES,
  BurdenEntradaCalculo,
  BurdenEntradaTc,
  BurdenEntradaTt,
  BurdenMedidorHomologado,
  BurdenResultadoCalculo,
  calcularBurdenDesdeEntrada,
  formatBurdenNum,
  longitudSugeridaDesdeProyecto,
} from "../../utils/burdenCalculo";
import {
  AWG_DEFAULT,
  CONDUCTORES_AWG_EMCALI,
  getConductorAwg,
} from "../../config/burdenCatalogoEmcali";
import BurdenMedidoresAdminModal from "./BurdenMedidoresAdminModal";
import EditIcon from "@mui/icons-material/Edit";

const getToken = () => {
  try {
    return atob(localStorage.getItem("authToken") || "") || "";
  } catch {
    return "";
  }
};

const getUser = () => {
  try {
    return atob(localStorage.getItem("user") || "") || "sistema";
  } catch {
    return "sistema";
  }
};

const NINGUNO = "ninguno";

export type BurdenHistorialItem = {
  id: string;
  fecha: string;
  usuario: string;
  entrada: BurdenEntradaCalculo;
  resultado: BurdenResultadoCalculo;
};

function entradaTcVacia(): BurdenEntradaTc {
  const cond = getConductorAwg(AWG_DEFAULT)!;
  return {
    tipo: "tc",
    i_secundaria_a: 5,
    medidor_ids: [NINGUNO, NINGUNO, NINGUNO],
    calibre_awg: AWG_DEFAULT,
    r_ac_ohm_por_m: cond.rAc75OhmPorM,
    longitud_conductor_m: 3,
    burden_nominal_va: 2.5,
    descripcion: "",
  };
}

function entradaTtVacia(): BurdenEntradaTt {
  const cond = getConductorAwg(AWG_DEFAULT)!;
  return {
    tipo: "tt",
    v_secundaria: 120,
    medidor_ids: [NINGUNO, NINGUNO, NINGUNO],
    calibre_awg: AWG_DEFAULT,
    r_ac_ohm_por_m: cond.rAc75OhmPorM,
    longitud_conductor_m: 3,
    burden_nominal_va: 2.5,
    descripcion: "",
  };
}

type Props = {
  proyectoId: number | null;
  proyectoCodigo?: string;
  proyectoNombre?: string;
  nivelTension?: string;
  distanciaRedM?: number | string | null;
  onHistorialChange?: (items: BurdenHistorialItem[]) => void;
  /** En la página /calculo-burden el botón va en el encabezado. */
  mostrarBotonMedidoresAdmin?: boolean;
};

function SeccionTitulo({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: "#1565c0",
        color: "#fff",
        px: 1.5,
        py: 0.75,
        borderRadius: "4px 4px 0 0",
        fontWeight: 700,
        fontSize: "0.85rem",
      }}
    >
      {children}
    </Box>
  );
}

function CampoResultado({ label, value, destacado }: { label: string; value: string; destacado?: boolean }) {
  return (
    <Box sx={{ p: 1, border: "1px solid #ddd", borderRadius: 1, bgcolor: destacado ? "#e8f5e9" : "#fafafa" }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={destacado ? 800 : 600}>
        {value}
      </Typography>
    </Box>
  );
}

export default function CalculoBurdenPanel({
  proyectoId,
  proyectoCodigo,
  proyectoNombre,
  nivelTension,
  distanciaRedM,
  onHistorialChange,
  mostrarBotonMedidoresAdmin = true,
}: Props) {
  const [subTab, setSubTab] = React.useState<"tc" | "tt">("tc");
  const [entradaTc, setEntradaTc] = React.useState<BurdenEntradaTc>(entradaTcVacia);
  const [entradaTt, setEntradaTt] = React.useState<BurdenEntradaTt>(entradaTtVacia);
  const [medidores, setMedidores] = React.useState<BurdenMedidorHomologado[]>([]);
  const [historial, setHistorial] = React.useState<BurdenHistorialItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [guardando, setGuardando] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState<string | null>(null);
  const [modalMedidoresOpen, setModalMedidoresOpen] = React.useState(false);
  const esAdmin = isProyectosAdminRole();

  const entrada: BurdenEntradaCalculo = subTab === "tt" ? entradaTt : entradaTc;
  const resultado = React.useMemo(
    () => calcularBurdenDesdeEntrada(entrada, medidores),
    [entrada, medidores],
  );

  const longitudProyecto = React.useMemo(
    () => longitudSugeridaDesdeProyecto(distanciaRedM),
    [distanciaRedM],
  );

  const cargarMedidores = React.useCallback(async () => {
    try {
      const q = esAdmin ? "?todos=1" : "";
      const res = await fetch(`${getProyectosApiBaseUrl()}/burden/medidores${q}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No se pudo cargar medidores.");
      setMedidores(Array.isArray(data.medidores) ? data.medidores : []);
    } catch (e: any) {
      setError(e.message || "Error al cargar medidores homologados.");
    }
  }, [esAdmin]);

  const cargarHistorial = React.useCallback(async () => {
    if (!proyectoId) {
      setHistorial([]);
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${getProyectosApiBaseUrl()}/${proyectoId}/burden/historial`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No fue posible cargar historial.");
      const items = Array.isArray(data.historial) ? data.historial : [];
      setHistorial(items);
      onHistorialChange?.(items);
    } catch (e: any) {
      setError(e.message || "Error al cargar historial.");
      setHistorial([]);
    } finally {
      setLoading(false);
    }
  }, [proyectoId, onHistorialChange]);

  React.useEffect(() => {
    void cargarMedidores();
  }, [cargarMedidores]);

  React.useEffect(() => {
    void cargarHistorial();
  }, [cargarHistorial]);

  React.useEffect(() => {
    if (longitudProyecto != null && longitudProyecto > 0) {
      setEntradaTc((p) => ({ ...p, longitud_conductor_m: longitudProyecto }));
      setEntradaTt((p) => ({ ...p, longitud_conductor_m: longitudProyecto }));
    }
  }, [longitudProyecto, proyectoId]);

  const setMedidorSlot = (idx: number, medidorId: string) => {
    const patch = (ids: string[]) => {
      const copy = [...ids];
      while (copy.length < 3) copy.push(NINGUNO);
      copy[idx] = medidorId || NINGUNO;
      return copy.slice(0, 3);
    };
    if (subTab === "tc") {
      setEntradaTc((p) => ({ ...p, medidor_ids: patch(p.medidor_ids) }));
    } else {
      setEntradaTt((p) => ({ ...p, medidor_ids: patch(p.medidor_ids) }));
    }
  };

  const setAwg = (awg: number) => {
    const cond = getConductorAwg(awg);
    if (!cond) return;
    if (subTab === "tc") {
      setEntradaTc((p) => ({
        ...p,
        calibre_awg: awg,
        r_ac_ohm_por_m: cond.rAc75OhmPorM,
      }));
    } else {
      setEntradaTt((p) => ({
        ...p,
        calibre_awg: awg,
        r_ac_ohm_por_m: cond.rAc75OhmPorM,
      }));
    }
  };

  const medidorIds = subTab === "tc" ? entradaTc.medidor_ids : entradaTt.medidor_ids;
  const campoVa = subTab === "tc" ? "cto_corriente_va" : "cto_tension_va";

  const guardarCalculo = async () => {
    if (!proyectoId) {
      setError("Seleccione un proyecto para guardar.");
      return;
    }
    if (!resultado) {
      setError("Revise los parámetros del cálculo.");
      return;
    }
    try {
      setGuardando(true);
      setError(null);
      const res = await fetch(`${getProyectosApiBaseUrl()}/${proyectoId}/burden`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          entrada,
          resultado,
          updated_by: getUser(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No se pudo guardar.");
      setOk("Cálculo guardado en historial.");
      await cargarHistorial();
    } catch (e: any) {
      setError(e.message || "Error al guardar.");
    } finally {
      setGuardando(false);
    }
  };

  const cargarDesdeHistorial = (item: BurdenHistorialItem) => {
    if (item.entrada.tipo === "tt") {
      setSubTab("tt");
      setEntradaTt(item.entrada as BurdenEntradaTt);
    } else {
      setSubTab("tc");
      setEntradaTc(item.entrada as BurdenEntradaTc);
    }
    setOk("Configuración cargada desde historial.");
  };

  if (!proyectoId) {
    return (
      <Alert severity="info">
        Seleccione un proyecto para calcular y guardar el burden (TC / TT).
      </Alert>
    );
  }

  const e = subTab === "tc" ? entradaTc : entradaTt;

  return (
    <Stack spacing={2}>
      {(proyectoCodigo || proyectoNombre) && (
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "#f8fafc" }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {proyectoCodigo ? `${proyectoCodigo} — ` : ""}
            {proyectoNombre}
          </Typography>
          {nivelTension && (
            <Typography variant="caption" color="text.secondary">
              Nivel de tensión: {nivelTension}
            </Typography>
          )}
        </Paper>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
        <Alert severity="info" sx={{ py: 0.5, flex: 1 }}>
          Plantilla EMCALI — Anexo 2. <strong>TC:</strong> VA<sub>total</sub> = VA<sub>medidor</sub> + I²×l×R<sub>ac</sub>.
          <strong> TT:</strong> VA<sub>total</sub> = VA<sub>medidor</sub> + [V/((R<sub>ac</sub>×l)+(V²/VA<sub>medidor</sub>))]²×(R<sub>ac</sub>×l).
          Cumple si el burden está entre 25 % y 100 % del nominal.
        </Alert>
        {esAdmin && mostrarBotonMedidoresAdmin && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setModalMedidoresOpen(true)}
            sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" } }}
          >
            Editar medidores
          </Button>
        )}
      </Stack>

      <BurdenMedidoresAdminModal
        open={modalMedidoresOpen}
        onClose={() => setModalMedidoresOpen(false)}
        onActualizado={() => void cargarMedidores()}
      />

      {error && <Alert severity="error">{error}</Alert>}
      {ok && (
        <Alert severity="success" onClose={() => setOk(null)}>
          {ok}
        </Alert>
      )}

      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)}>
        <Tab label="TC — Transformador de corriente" value="tc" />
        <Tab label="TT — Transformador de tensión" value="tt" />
      </Tabs>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <SeccionTitulo>Cálculo carga del medidor</SeccionTitulo>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1}>
            {[0, 1, 2].map((idx) => (
              <Grid item xs={12} md={4} key={`med-${idx}`}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={`Medidor ${idx + 1}`}
                  value={medidorIds[idx] || NINGUNO}
                  onChange={(ev) => setMedidorSlot(idx, ev.target.value)}
                >
                  <MenuItem value={NINGUNO}>Ninguno</MenuItem>
                  {medidores.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.nombre} — {formatBurdenNum(m[campoVa], 3)} VA
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ))}
            <Grid item xs={12} md={4}>
              <CampoResultado
                label="Consumo de potencia (suma medidores) [VA]"
                value={formatBurdenNum(resultado?.va_medidor ?? 0, 4)}
                destacado
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <SeccionTitulo>Cálculo carga del conductor</SeccionTitulo>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.5}>
            {subTab === "tc" && (
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Corriente nominal secundario (A)"
                  value={entradaTc.i_secundaria_a}
                  onChange={(ev) =>
                    setEntradaTc((p) => ({ ...p, i_secundaria_a: Number(ev.target.value) }))
                  }
                >
                  {BURDEN_I_SECUNDARIA_OPCIONES.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v} A
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {subTab === "tt" && (
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Tensión secundaria TP (V)"
                  value={entradaTt.v_secundaria}
                  onChange={(ev) =>
                    setEntradaTt((p) => ({ ...p, v_secundaria: Number(ev.target.value) }))
                  }
                >
                  {BURDEN_V_SECUNDARIA_OPCIONES.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v} V
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Calibre conductor (AWG)"
                value={e.calibre_awg}
                onChange={(ev) => setAwg(Number(ev.target.value))}
              >
                {CONDUCTORES_AWG_EMCALI.map((c) => (
                  <MenuItem key={c.awg} value={c.awg}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="R_ac cobre a 75 °C (Ω/m)"
                type="number"
                inputProps={{ step: 0.00001, min: 0 }}
                value={e.r_ac_ohm_por_m}
                onChange={(ev) => {
                  const v = Number(ev.target.value);
                  if (subTab === "tc") setEntradaTc((p) => ({ ...p, r_ac_ohm_por_m: v }));
                  else setEntradaTt((p) => ({ ...p, r_ac_ohm_por_m: v }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Longitud total conductor (ida + retorno) [m]"
                type="number"
                inputProps={{ step: 0.1, min: 0 }}
                value={e.longitud_conductor_m}
                onChange={(ev) => {
                  const v = Number(ev.target.value);
                  if (subTab === "tc") setEntradaTc((p) => ({ ...p, longitud_conductor_m: v }));
                  else setEntradaTt((p) => ({ ...p, longitud_conductor_m: v }));
                }}
                helperText={
                  longitudProyecto != null
                    ? `Sugerida desde solicitud: ${formatBurdenNum(longitudProyecto, 1)} m`
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CampoResultado
                label="Carga del conductor (VA)"
                value={formatBurdenNum(resultado?.va_conductor ?? 0, 5)}
                destacado
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <SeccionTitulo>Burden total y verificación</SeccionTitulo>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label={`Burden nominal ${subTab.toUpperCase()} (VA)`}
                value={e.burden_nominal_va}
                onChange={(ev) => {
                  const v = Number(ev.target.value);
                  if (subTab === "tc") setEntradaTc((p) => ({ ...p, burden_nominal_va: v }));
                  else setEntradaTt((p) => ({ ...p, burden_nominal_va: v }));
                }}
              >
                {BURDEN_NOMINAL_OPCIONES.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v} VA
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <CampoResultado
                label="Burden total (VA)"
                value={formatBurdenNum(resultado?.burden_total_va ?? 0, 4)}
                destacado
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <CampoResultado
                label="Mínimo 25 % (VA)"
                value={formatBurdenNum(resultado?.burden_minimo_va ?? 0, 4)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <CampoResultado
                label="Nominal 100 % (VA)"
                value={formatBurdenNum(resultado?.burden_nominal_va ?? 0, 4)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Box
                sx={{
                  p: 1.5,
                  textAlign: "center",
                  borderRadius: 1,
                  bgcolor: resultado?.cumple ? "#c8e6c9" : "#ffcdd2",
                  border: "1px solid",
                  borderColor: resultado?.cumple ? "#81c784" : "#e57373",
                }}
              >
                <Typography variant="caption" display="block">
                  Criterio de cumplimiento
                </Typography>
                <Typography variant="h6" fontWeight={800}>
                  {resultado?.cumple ? "Cumple" : "No cumple"}
                </Typography>
                {resultado && (
                  <Typography variant="caption">
                    {formatBurdenNum(resultado.pct_nominal, 1)} % del nominal
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Descripción / observación"
                value={e.descripcion || ""}
                onChange={(ev) => {
                  const v = ev.target.value;
                  if (subTab === "tc") setEntradaTc((p) => ({ ...p, descripcion: v }));
                  else setEntradaTt((p) => ({ ...p, descripcion: v }));
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Button variant="contained" disabled={guardando || !resultado} onClick={() => void guardarCalculo()}>
        {guardando ? "Guardando…" : `Guardar cálculo ${subTab.toUpperCase()}`}
      </Button>

      <Divider />

      <Typography variant="subtitle2" fontWeight={700}>
        Historial
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !historial.length ? (
        <Typography variant="body2" color="text.secondary">
          Sin cálculos guardados.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {historial.map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={(item.entrada?.tipo || "tc").toUpperCase()}
                      color="primary"
                      variant="outlined"
                    />
                    <Typography variant="body2" fontWeight={700}>
                      {new Date(item.fecha).toLocaleString("es-CO")}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {item.usuario} · Total {formatBurdenNum(item.resultado?.burden_total_va ?? 0, 4)} VA ·{" "}
                    {item.resultado?.cumple ? "Cumple" : "No cumple"}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={() => cargarDesdeHistorial(item)}>
                  Cargar
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
